import { useForm } from '@inertiajs/react'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatCurrency, formatDate } from '@/lib/utils'

export type TaxRegistrationProgress = {
  tax_registration_type_id: string
  code: string
  name: string
  total_fee_cents: number
  completed: boolean
  reference_number: string | null
  completed_at: string | null
}

function CompleteRegistrationForm({ formationId, registration }: { formationId: string; registration: TaxRegistrationProgress }) {
  // Nested explicitly rather than left to Rails' param-wrapping — see
  // RegisteredAgentAddressSheet for why relying on that inference is fragile.
  const { data, setData, post, processing, errors } = useForm({
    tax_registration_completion: { tax_registration_type_id: registration.tax_registration_type_id, reference_number: '' }
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(`/backoffice/formations/${formationId}/tax_registration_completions`)
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 shrink-0">
      <Input
        placeholder="Reference number"
        value={data.tax_registration_completion.reference_number}
        onChange={(e) => setData('tax_registration_completion', { ...data.tax_registration_completion, reference_number: e.target.value })}
        aria-label={`Reference number for ${registration.name}`}
        className="h-8 text-xs w-40"
      />
      <Button type="submit" size="sm" className="h-8 text-xs" disabled={processing}>
        {processing ? 'Saving...' : 'Mark complete'}
      </Button>
      {errors['tax_registration_completion.tax_registration_type_id'] && (
        <p className="text-xs text-destructive">{errors['tax_registration_completion.tax_registration_type_id']}</p>
      )}
    </form>
  )
}

type Props = {
  formationId: string
  taxRegistrationProgress: TaxRegistrationProgress[]
}

// The EIN is one of these types (and also the tracker's "EIN issued"
// milestone — see TaxRegistrationType::EIN_CODE); the others (state tax
// ID, sales permit, SUTA, ITIN) apply the same way but aren't a numbered
// milestone.
export function TaxRegistrationSection({ formationId, taxRegistrationProgress }: Props) {
  if (taxRegistrationProgress.length === 0) return null

  return (
    <section className="border rounded-lg">
      <div className="px-4 py-3 border-b">
        <h2 className="text-sm font-medium text-foreground">EIN application</h2>
      </div>
      <div className="divide-y">
        {taxRegistrationProgress.map((registration) => (
          <div key={registration.tax_registration_type_id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">{registration.name}</div>
              <div className="text-xs text-muted-foreground">
                {registration.completed
                  ? `Complete${registration.completed_at ? ` · ${formatDate(registration.completed_at)}` : ''}${registration.reference_number ? ` · ${registration.reference_number}` : ''}`
                  : formatCurrency(registration.total_fee_cents)}
              </div>
            </div>
            {registration.completed ? (
              <span className="flex items-center gap-1 text-xs text-green-600 font-medium shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                Done
              </span>
            ) : (
              <CompleteRegistrationForm formationId={formationId} registration={registration} />
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
